import { Component, Input, OnInit } from '@angular/core';
import { ModalService, MODAL } from '../../services/modal.service';
import { CustomerService } from '../../services/customer.service';
import { environment } from '../../../environments/environment';
import { GTM } from 'src/app/services/gtm.service';
import { CreateCustomer } from 'src/app/services/kingfisher/create-customer';
import { HbmApiService } from 'src/app/services/hbm/hbm-api.service';
import { Country, otherCountries, prioCountries } from 'src/app/services/kingfisher/countries';
import { HonorificPrefix } from 'src/app/services/kingfisher/customer';
import { TranslateService } from '@ngx-translate/core';
import { faEnvelope, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FormGroup } from '@angular/forms';
import { BuildMarketingConsentFormGroup } from '../../models/register/form-groups';

type RadioButtonValue = {
  name: string;
  value: HonorificPrefix;
};

@Component({
  selector: 'ng-casto-register-customer',
  templateUrl: './register-customer.component.html',
})
export class RegisterCustomerComponent implements OnInit {
  @Input() public isModal = true;
  prioCountries: Country[] = prioCountries;
  otherCountries: Country[] = otherCountries;
  isVendor = environment.vendor;

  public inputs: any;
  public isLoading: boolean;
  loginCallBack: Function;
  public globalError: string = '';
  public showBackToLogin: boolean;
  public marketingConsentForm: FormGroup;

  passwordVisible: boolean = false;
  confirmPasswordVisible: boolean = false;

  protected countries: Country[] = [];
  protected readonly faEnvelope = faEnvelope;
  protected readonly faEye = faEye;
  protected readonly faEyeSlash = faEyeSlash;
  protected civilityValues: RadioButtonValue[] = [
    {
      name: this.translate.instant('customer-info-form.civility-form.m'),
      value: HonorificPrefix.Monsieur,
    },
    {
      name: this.translate.instant('customer-info-form.civility-form.mme'),
      value: HonorificPrefix.Madame,
    },
    {
      name: this.translate.instant('customer-info-form.civility-form.mmle'),
      value: HonorificPrefix.Mademoiselle,
    },
  ];

  constructor(
    public modalService: ModalService,
    private readonly customer: CustomerService,
    private readonly gtm: GTM,
    private readonly hbmService: HbmApiService,
    private translate: TranslateService,
  ) { }

  resetInputError() {
    this.globalError = '';
    for (let key in this.inputs) {
      this.inputs[key].error = false;
    }
  }

  close() {
    this.modalService.close();
  }

  ngOnInit(): void {
    this.initCountries();
    this.marketingConsentForm = BuildMarketingConsentFormGroup();
    this.gtm.trackEvent('s-enregistrer');
    this.showBackToLogin = !environment.vendor;
    if (this.modalService.currentDatas) this.loginCallBack = this.modalService.currentDatas.loginCallBack;

    this.inputs = {
      civilite: {
        value: null,
        error: false,
      },
      captcha: {
        value: '',
        error: '',
      },
      nom: {
        value: '',
        error: false,
      },
      prenom: {
        value: '',
        error: false,
      },
      telephone: {
        value: '',
        error: false,
        validated: false
      },
      email: {
        value: '',
        error: false,
        validated: false
      },
      confirmeEmail: {
        value: '',
        error: false,
        validated: false
      },
      password: {
        value: '',
        error: false,
        validated: false
      },
      confirmePassword: {
        value: '',
        error: false,
        validated: false
      },
      adresse: {
        value: '',
        error: false,
      },
      complement: {
        value: '',
        error: false,
      },
      codePostal: {
        value: '',
        error: false,
        validated: false
      },
      ville: {
        value: '',
        error: false,
      },
      pays: {
        value: '',
        error: false,
      },
      inscriptionNewsletter: {
        value: false,
        error: false,
      },
      inscriptionNewsletterPartenaires: {
        value: false,
        error: false,
      },
      acceptRecontact: {
        value: false,
        error: false,
      },
    };
  }

  login() {
    this.modalService.display(MODAL.LOGIN);
  }

  isFormValid() {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{13,34}$/;
    const regexPhoneNumber = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    const regexPostalCodeFr = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/;
    const emptyRegex = /^\s*$/;

    this.inputs.civilite.error = this.inputs.civilite.value == null;
    this.inputs.nom.error = emptyRegex.test(this.inputs.nom.value);
    this.inputs.prenom.error = emptyRegex.test(this.inputs.prenom.value);
    this.inputs.telephone.error = !regexPhoneNumber.test(this.inputs.telephone.value);

    if (!this.isVendor) {
      this.inputs.password.error = !passwordRegex.test(this.inputs.password.value);
      this.inputs.confirmePassword.error = this.inputs.password.value !== this.inputs.confirmePassword.value;
    }

    this.inputs.adresse.error = emptyRegex.test(this.inputs.adresse.value);
    this.inputs.ville.error = emptyRegex.test(this.inputs.ville.value);
    this.inputs.pays.error = emptyRegex.test(this.inputs.pays.value);

    if (this.inputs.pays.value === 'FR' && this.inputs.codePostal.error === false) {
      this.inputs.codePostal.error = !regexPostalCodeFr.test(this.inputs.codePostal.value);
    }

    this.inputs.confirmeEmail.error = this.inputs.email.value !== this.inputs.confirmeEmail.value;
    this.inputs.email.error = !emailRegex.test(this.inputs.email.value);

    for (let key in this.inputs) {
      if (this.inputs[key].error) {
        return false;
      }
    }
    return true;
  }

  checkIfPasswordContainLowercase(): boolean {
    const password = this.inputs.password.value;
    return /[a-z]/.test(password);
  }

  checkIfPasswordContainUppercase(): boolean {
    const password = this.inputs.password.value;
    return /[A-Z]/.test(password);
  }

  checkIfPasswordContainNumber(): boolean {
    const password = this.inputs.password.value;
    return /\d/.test(password);
  }

  checkIfPasswordContainSpecialCharacter(): boolean {
    const password = this.inputs.password.value;
    return /[$@!%*?&]/.test(password);
  }

  checkIfPasswordHasMinLength(): boolean {
    const password = this.inputs.password.value;
    return password?.length >= 13;
  }

  validateConfirmPassword() {
    this.inputs.confirmePassword.validated = true;
    this.inputs.confirmePassword.error = this.inputs.confirmePassword.value !== this.inputs.password.value;
  }

  validatePhone() {
    const regexPhoneNumber = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    this.inputs.telephone.validated = true;
    this.inputs.telephone.error = !regexPhoneNumber.test(this.inputs.telephone.value);
  }

  validatePostalCode() {
    const regexPostalCodeFr = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/; // France uniquement
    this.inputs.codePostal.validated = true;
    this.inputs.codePostal.error = !regexPostalCodeFr.test(this.inputs.codePostal.value);
  }

  validateEmail() {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.inputs.email.validated = true;
    this.inputs.email.error = !emailRegex.test(this.inputs.email.value);
    if (this.inputs.confirmeEmail.validated) {
      this.validateConfirmEmail();
    }
  }

  validateConfirmEmail() {
    this.inputs.confirmeEmail.validated = true;
    this.inputs.confirmeEmail.error = this.inputs.email.value !== this.inputs.confirmeEmail.value;
  }

  async submit() {
    this.resetInputError();

    if (this.isFormValid()) {
      const date = new Date().toISOString().replace('Z', '');
      const data: CreateCustomer = {
        honorificPrefix: this.inputs.civilite.value,
        email: this.inputs.email.value,
        familyName: this.inputs.nom.value,
        givenName: this.inputs.prenom.value,
        phoneNumber: this.inputs.telephone.value,
        address: {
          line1: this.inputs.adresse.value,
          complement: this.inputs.complement.value,
          postalCode: this.inputs.codePostal.value,
          city: this.inputs.ville.value,
          country: this.inputs.pays.value,
        },
        marketingChannels: [
          {
            channel: 'sms',
            optIn: this.marketingConsentForm.get('sms').value,
            submitted: date,
            privacyPolicyVersionId: '1',
            marketingChannelVersionId: '2',
          },
          {
            channel: 'phone',
            optIn: this.marketingConsentForm.get('phone').value,
            submitted: date,
            privacyPolicyVersionId: '1',
            marketingChannelVersionId: '2',
          },
          {
            channel: 'email',
            optIn: this.marketingConsentForm.get('email').value,
            submitted: date,
            privacyPolicyVersionId: '1',
            marketingChannelVersionId: '2',
          },
          {
            channel: 'post',
            optIn: this.marketingConsentForm.get('post').value,
            submitted: date,
            privacyPolicyVersionId: '1',
            marketingChannelVersionId: '2',
          },
        ],
      };

      if (!this.isVendor) {
        data.password = this.inputs.password.value;
      }

      this.isLoading = true;

      try {
        await this.customer.register(data);
        await this.hbmService.acquireTokenWS(this.customer.userDatas.id, 'signup');
        if (this.loginCallBack) {
          this.loginCallBack();
        } else {
          this.modalService.close();
        }
      } catch (err) {
        logError(err);
        document.querySelector('ng-casto-modals').scrollTo({ top: 0, behavior: 'smooth' });
        this.globalError = err;
      } finally {
        this.isLoading = false;
      }
    } else {
      document.querySelector('ng-casto-modals').scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private initCountries() {
    this.countries = this.countries.concat(prioCountries, otherCountries);
  }

}
